import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from 'lucide-react';
import type { WorkflowJob } from '../../graphql/types';
import {
  GuidelinesApiError,
  getTitleAnalysisResults,
  runTitleAnalysis,
  type TitleAnalysisResultsResponse,
} from '../../services/guidelinesApiService';
import {
  ALLOWED_USE_CASES,
  USE_CASE_LABELS,
  downloadFileName,
  isRunControlDisabled,
  isTitleAnalysisRunStale,
} from '../../titleAnalysis';
import { adaptTitleAnalysisReport } from './reportAdapter';
import { displayValue } from './displayUtils/displayValue';
import { displayList } from './displayUtils/displayList';
import TitleAnalysisDisclaimer from './TitleAnalysisDisclaimer';
import TitleAnalysisGrade from './TitleAnalysisGrade';
import TitleAnalysisRationale from './TitleAnalysisRationale';
import TitleAnalysisSummary from './TitleAnalysisSummary';
import TitleAnalysisSubjectLienPosition from './TitleAnalysisSubjectLienPosition';
import TitleAnalysisCurrentVestedOwner from './TitleAnalysisCurrentVestedOwner';
import TitleAnalysisSearchCoverage from './TitleAnalysisSearchCoverage';
import TitleAnalysisExceptionsGrouped from './TitleAnalysisExceptionsGrouped';
import TitleAnalysisVestingChain from './TitleAnalysisVestingChain';
import TitleAnalysisEncumbrances from './TitleAnalysisEncumbrances';
import TitleAnalysisEntityResolution from './TitleAnalysisEntityResolution';
import TitleAnalysisCommitmentReconciliation from './TitleAnalysisCommitmentReconciliation';

/**
 * TitleAnalysisSection
 *
 * Runs the Title Analysis agent for a TR job and displays the resulting report.
 *
 * State machine: loading | idle | starting | running | stale | completed |
 * failed | loadError. A stored report stays on screen during a re-run until
 * the new report loads.
 *
 * Requirements: 18.3, 18.4, 18.5, 18.6, 18.7, 18.8, 18.9, 18.20, 18.23, 18.24, 18.25
 */

export type TitleAnalysisPhase =
  | 'loading'
  | 'idle'
  | 'starting'
  | 'running'
  | 'stale'
  | 'completed'
  | 'failed'
  | 'loadError';

/** Poll cadence while a run is active (Requirement 18.6). */
export const TITLE_ANALYSIS_POLL_INTERVAL_MS = 5000;

export const MAX_CONSECUTIVE_POLL_ERRORS = 5;

const LIEN_STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  mismatch: 'Mismatch',
  undetermined: 'Undetermined',
};

const START_ERROR_MESSAGE = 'The analysis could not be started.';
const LOAD_ERROR_MESSAGE = 'The title analysis could not be loaded.';
const DID_NOT_FINISH_MESSAGE = 'The analysis did not finish.';
const STALE_MESSAGE = 'The previous run did not finish; you can retry.';
const ALREADY_RUNNING_MESSAGE = 'An analysis is already running for this job.';

export function phaseFromResults(
  results: TitleAnalysisResultsResponse,
  nowMs: number
): TitleAnalysisPhase {
  switch (results.title_analysis_status) {
    case 'in_progress':
      return isTitleAnalysisRunStale(
        results.title_analysis_started_at,
        results.stale_after_seconds,
        nowMs
      )
        ? 'stale'
        : 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return 'idle';
  }
}

interface TitleAnalysisSectionProps {
  job: WorkflowJob;
}

const buttonClass =
  'px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButtonClass =
  'px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

const PDF_ERROR_MESSAGE = 'The PDF could not be generated.';

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const TitleAnalysisSection: React.FC<TitleAnalysisSectionProps> = ({ job }) => {
  const [phase, setPhaseState] = useState<TitleAnalysisPhase>('loading');
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(job.useCase ?? null);
  const [results, setResults] = useState<TitleAnalysisResultsResponse | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const phaseRef = useRef<TitleAnalysisPhase>(phase);
  const setPhase = useCallback((next: TitleAnalysisPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const mountedRef = useRef(true);
  const loadSeqRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const pollErrorsRef = useRef(0);

  const { jobId } = job;

  const applyResults = useCallback(
    (loaded: TitleAnalysisResultsResponse) => {
      pollErrorsRef.current = 0;
      setResults(loaded);
      setPhase(phaseFromResults(loaded, Date.now()));
    },
    [setPhase]
  );

  const loadResults = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setPhase('loading');
    try {
      const loaded = await getTitleAnalysisResults(jobId);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      applyResults(loaded);
    } catch (err) {
      console.error('[TitleAnalysisSection] Failed to load results:', err);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      setPhase('loadError');
    }
  }, [jobId, applyResults, setPhase]);

  // Initial load of any stored report.
  useEffect(() => {
    mountedRef.current = true;
    void loadResults();
    return () => {
      mountedRef.current = false;
    };
    // Mount-only: later status changes are driven by this component's own polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the results route every 5 seconds, only while a run is active (Requirement 18.6, 18.8).
  useEffect(() => {
    if (phase !== 'running') return undefined;

    const tick = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const loaded = await getTitleAnalysisResults(jobId);
        if (!mountedRef.current || phaseRef.current !== 'running') return;
        applyResults(loaded);
      } catch (err) {
        console.error('[TitleAnalysisSection] Poll failed:', err);
        if (!mountedRef.current || phaseRef.current !== 'running') return;
        pollErrorsRef.current += 1;
        if (pollErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) setPhase('loadError');
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const id = setInterval(() => void tick(), TITLE_ANALYSIS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, jobId, applyResults, setPhase]);

  const handleRun = useCallback(async () => {
    if (isRunControlDisabled(selectedUseCase, phaseRef.current === 'starting')) return;
    const previous = phaseRef.current;
    setStartError(null);
    setNotice(null);
    setPhase('starting');
    try {
      const dispatched = await runTitleAnalysis({
        job_id: jobId,
        use_case: selectedUseCase ?? undefined,
      });
      if (!mountedRef.current) return;
      if (dispatched.already_running) setNotice(ALREADY_RUNNING_MESSAGE);
      pollErrorsRef.current = 0;
      setPhase('running');
    } catch (err) {
      console.error('[TitleAnalysisSection] Failed to start analysis:', err);
      if (!mountedRef.current) return;
      const detail =
        err instanceof GuidelinesApiError && err.serverMessage ? ` ${err.serverMessage}` : '';
      setStartError(`${START_ERROR_MESSAGE}${detail}`);
      // Re-enable the run control and keep any displayed report unchanged.
      setPhase(previous === 'starting' ? 'idle' : previous);
    }
  }, [jobId, selectedUseCase, setPhase]);

  const report = results?.report ?? null;

  const adapted = useMemo(
    () => (report ? adaptTitleAnalysisReport(report, job) : null),
    [report, job]
  );

  const handleDownload = useCallback(() => {
    if (!report) return;
    const body = JSON.stringify(report, null, 2);
    const blob = new Blob([body], { type: 'application/json' });
    triggerDownload(blob, downloadFileName(job.loanNumber, report.analysis_date));
  }, [report, job.loanNumber]);

  const handleDownloadPdf = useCallback(async () => {
    if (!report || isGeneratingPdf) return;
    setPdfError(null);
    setIsGeneratingPdf(true);
    try {
      // Loaded on demand so the PDF libraries stay out of the main bundle.
      const { buildTitleAnalysisPdf, pdfFileName } = await import('./exportPdf');
      const blob = buildTitleAnalysisPdf(report, job).output('blob');
      triggerDownload(blob, pdfFileName(job.loanNumber, report.analysis_date));
    } catch (err) {
      console.error('[TitleAnalysisSection] Failed to generate PDF:', err);
      if (mountedRef.current) setPdfError(PDF_ERROR_MESSAGE);
    } finally {
      if (mountedRef.current) setIsGeneratingPdf(false);
    }
  }, [report, job, isGeneratingPdf]);

  const isActive = phase === 'loading' || phase === 'starting' || phase === 'running';
  const runDisabled = isRunControlDisabled(selectedUseCase, isActive);
  const runLabel =
    phase === 'failed' || phase === 'stale'
      ? 'Retry'
      : report
        ? 'Re-run'
        : 'Run Title Analysis';

  return (
    <section
      aria-labelledby="title-analysis-heading"
      className="mt-8 space-y-4"
      data-testid="title-analysis-section"
    >
      <h2 id="title-analysis-heading" className="text-xl font-semibold text-gray-900">
        Title Analysis
      </h2>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label
            htmlFor="title-analysis-use-case"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Use case
          </label>
          <select
            id="title-analysis-use-case"
            value={selectedUseCase ?? ''}
            onChange={(e) => setSelectedUseCase(e.target.value || null)}
            disabled={isActive}
            className="block rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm"
          >
            <option value="">Select a use case</option>
            {ALLOWED_USE_CASES.map((uc) => (
              <option key={uc} value={uc}>
                {USE_CASE_LABELS[uc] ?? uc}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className={buttonClass} disabled={runDisabled} onClick={() => void handleRun()}>
          {runLabel}
        </button>

        {report && (
          <>
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={isGeneratingPdf}
              onClick={() => void handleDownloadPdf()}
            >
              {isGeneratingPdf ? 'Preparing PDF?' : 'Download PDF'}
            </button>
            <button type="button" className={secondaryButtonClass} onClick={handleDownload}>
              Download JSON
            </button>
          </>
        )}
      </div>

      {pdfError && (
        <div role="alert" className="text-sm text-red-700">
          {pdfError}
        </div>
      )}

      {startError && (
        <div role="alert" className="text-sm text-red-700">
          {startError}
        </div>
      )}

      {phase === 'loading' && (
        <div role="status" className="flex items-center gap-2 text-sm text-gray-700">
          <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Loading title analysis…</span>
        </div>
      )}

      {(phase === 'starting' || phase === 'running') && (
        <div role="status" className="flex items-center gap-2 text-sm text-gray-700">
          <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>
            {phase === 'starting' ? 'Starting title analysis…' : 'Title analysis is running…'}
          </span>
        </div>
      )}

      {phase === 'running' && notice && (
        <div role="status" className="text-sm text-blue-700">
          {notice}
        </div>
      )}

      {phase === 'stale' && (
        <div role="alert" className="text-sm text-red-700">
          {STALE_MESSAGE}
        </div>
      )}

      {phase === 'failed' && (
        <div role="alert" className="text-sm text-red-700">
          {results?.title_analysis_error || DID_NOT_FINISH_MESSAGE}
        </div>
      )}

      {(phase === 'loadError' || (phase === 'completed' && !report)) && (
        <div role="alert" className="flex items-center gap-3 text-sm text-red-700">
          <span>{LOAD_ERROR_MESSAGE}</span>
          <button type="button" className={secondaryButtonClass} onClick={() => void loadResults()}>
            Reload
          </button>
        </div>
      )}

      {adapted && phase !== 'completed' && phase !== 'loading' && (
        <div role="status" className="text-sm text-gray-600">
          {phase === 'running' || phase === 'starting'
            ? 'Updating: showing the previous report until the new analysis finishes.'
            : 'Showing the report from the last successful run.'}
        </div>
      )}

      {adapted && (
        <div className="space-y-4" data-testid="title-analysis-report">
          <TitleAnalysisDisclaimer />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TitleAnalysisGrade grade={adapted.grade} />
            <div className="md:col-span-2">
              <TitleAnalysisRationale rationale={adapted.gradeRationale} />
            </div>
          </div>
          <TitleAnalysisSummary summary={adapted.summary} />
          <TitleAnalysisSubjectLienPosition {...adapted.subjectLoan} />
          <div className="bg-white rounded-lg shadow p-4 text-sm text-gray-700">
            <div>
              <span className="font-medium">Expected position:</span>{' '}
              {displayValue(adapted.lienPosition.expected)}
            </div>
            <div>
              <span className="font-medium">Derived position:</span>{' '}
              {displayValue(adapted.lienPosition.derived)}
            </div>
            <div>
              <span className="font-medium">Status:</span>{' '}
              {LIEN_STATUS_LABELS[adapted.lienPosition.status] ?? adapted.lienPosition.status}
            </div>
            <div>
              <span className="font-medium">Senior open encumbrances:</span>{' '}
              {displayList(adapted.lienPosition.seniorOpenEncumbranceIds)}
            </div>
            <div>
              <span className="font-medium">Explanation:</span>{' '}
              {displayValue(adapted.lienPosition.explanation)}
            </div>
          </div>
          <TitleAnalysisCurrentVestedOwner {...adapted.currentVestedOwner} />
          <TitleAnalysisSearchCoverage {...adapted.searchCoverage} />
          <TitleAnalysisExceptionsGrouped exceptions={adapted.exceptions} />
          <TitleAnalysisVestingChain vestingChain={adapted.vestingChain} />
          <TitleAnalysisEncumbrances encumbrances={adapted.encumbrances} />
          <TitleAnalysisEntityResolution entities={adapted.entities} />
          <TitleAnalysisCommitmentReconciliation reconciliation={adapted.commitment} />
        </div>
      )}
    </section>
  );
};

export default TitleAnalysisSection;
